/**
 * Web Weather Service — fetches airport weather and map advisories from AWC.
 * Uses the shared-core ResponseParser for METAR and TAF parsing.
 */
import {
  parseMetars, parseTafs,
  type ParsedMetar, type ParsedTaf,
  type RawMetarResponse, type RawTafResponse,
} from '@efb/shared-core';
import type { FeatureCollection } from 'geojson';

// AviationWeather.gov does not expose browser CORS headers. The deployed web
// server proxies this same-origin path to https://aviationweather.gov/api/data.
const AWC_BASE = '/api/weather';

export interface WebWeatherData {
  metars: ParsedMetar[];
  tafs: ParsedTaf[];
  fetchedAt: number;
}

export interface WeatherMapData {
  sigmets: FeatureCollection;
  airmets: FeatureCollection;
  pireps: FeatureCollection;
  warnings: string[];
  fetchedAt: number;
}

export function buildAwcBbox(
  minLat: number, maxLat: number, minLon: number, maxLon: number,
): string {
  return `${minLat},${minLon},${maxLat},${maxLon}`;
}

export function isFeatureCollection(value: unknown): value is FeatureCollection {
  return Boolean(
    value &&
    typeof value === 'object' &&
    (value as FeatureCollection).type === 'FeatureCollection' &&
    Array.isArray((value as FeatureCollection).features),
  );
}

function emptyFeatureCollection(): FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

export class WebWeatherService {
  private cache: Map<string, WebWeatherData> = new Map();
  private mapCache: Map<string, WeatherMapData> = new Map();

  async fetchForStation(stationId: string): Promise<WebWeatherData> {
    const cached = this.cache.get(stationId);
    if (cached && Date.now() - cached.fetchedAt < 10 * 60 * 1000) return cached;

    const [metarRes, tafRes] = await Promise.allSettled([
      fetch(`${AWC_BASE}/metar?ids=${stationId}&format=json`),
      fetch(`${AWC_BASE}/taf?ids=${stationId}&format=json`),
    ]);

    let metars: ParsedMetar[] = [];
    let tafs: ParsedTaf[] = [];

    if (metarRes.status === 'fulfilled' && metarRes.value.ok) {
      try {
        const raw: RawMetarResponse[] = await metarRes.value.json();
        metars = parseMetars(raw);
      } catch { /* parse error */ }
    }

    if (tafRes.status === 'fulfilled' && tafRes.value.ok) {
      try {
        const raw: RawTafResponse[] = await tafRes.value.json();
        tafs = parseTafs(raw);
      } catch { /* parse error */ }
    }

    const data: WebWeatherData = { metars, tafs, fetchedAt: Date.now() };
    this.cache.set(stationId, data);
    return data;
  }

  async fetchForBbox(
    minLat: number, maxLat: number, minLon: number, maxLon: number,
  ): Promise<WebWeatherData> {
    const bbox = buildAwcBbox(minLat, maxLat, minLon, maxLon);
    const cached = this.cache.get(bbox);
    if (cached && Date.now() - cached.fetchedAt < 10 * 60 * 1000) return cached;

    const [metarRes, tafRes] = await Promise.allSettled([
      fetch(`${AWC_BASE}/metar?bbox=${bbox}&format=json`),
      fetch(`${AWC_BASE}/taf?bbox=${bbox}&format=json`),
    ]);

    let metars: ParsedMetar[] = [];
    let tafs: ParsedTaf[] = [];

    if (metarRes.status === 'fulfilled' && metarRes.value.ok) {
      try { metars = parseMetars(await metarRes.value.json()); } catch { /* */ }
    }
    if (tafRes.status === 'fulfilled' && tafRes.value.ok) {
      try { tafs = parseTafs(await tafRes.value.json()); } catch { /* */ }
    }

    const data: WebWeatherData = { metars, tafs, fetchedAt: Date.now() };
    this.cache.set(bbox, data);
    return data;
  }

  async fetchMapLayersForBbox(
    minLat: number, maxLat: number, minLon: number, maxLon: number,
  ): Promise<WeatherMapData> {
    const bbox = buildAwcBbox(minLat, maxLat, minLon, maxLon);
    const cached = this.mapCache.get(bbox);
    if (cached && Date.now() - cached.fetchedAt < 5 * 60 * 1000) return cached;

    const [sigmetResult, airmetResult, pirepResult] = await Promise.allSettled([
      this.fetchFeatureCollection(`${AWC_BASE}/airsigmet?format=geojson`),
      this.fetchFeatureCollection(`${AWC_BASE}/gairmet?format=geojson`),
      this.fetchFeatureCollection(`${AWC_BASE}/pirep?bbox=${bbox}&format=geojson&age=2`),
    ]);

    const failures = [sigmetResult, airmetResult, pirepResult]
      .filter((result) => result.status === 'rejected');
    if (failures.length === 3) {
      throw new Error('Current map weather is temporarily unavailable');
    }

    const warnings: string[] = [];
    if (sigmetResult.status === 'rejected') warnings.push('SIGMET data unavailable');
    if (airmetResult.status === 'rejected') warnings.push('G-AIRMET data unavailable');
    if (pirepResult.status === 'rejected') warnings.push('PIREP data unavailable');

    const data: WeatherMapData = {
      sigmets: sigmetResult.status === 'fulfilled' ? sigmetResult.value : emptyFeatureCollection(),
      airmets: airmetResult.status === 'fulfilled' ? airmetResult.value : emptyFeatureCollection(),
      pireps: pirepResult.status === 'fulfilled' ? pirepResult.value : emptyFeatureCollection(),
      warnings,
      fetchedAt: Date.now(),
    };
    this.mapCache.set(bbox, data);
    return data;
  }

  clearCache(): void {
    this.cache.clear();
    this.mapCache.clear();
  }

  private async fetchFeatureCollection(url: string): Promise<FeatureCollection> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Weather request failed (${response.status})`);
    const data: unknown = await response.json();
    if (!isFeatureCollection(data)) throw new Error('Weather response was not valid GeoJSON');
    return data;
  }
}
